import { useQueryClient } from '@tanstack/react-query';
import { ChannelClientQueryKeys } from 'gcs-common/clients/api/entities/channels/channel-api';
import { useGetBeamerChatEnabled } from 'gcs-common/clients/api/entities/current-user/current-user-api-selectors';
import { useGetETSTicketNotificationsCountForChannelSelector } from 'gcs-common/clients/api/entities/tickets/tickets-api-selectors';
import {
  BEAMER_CHANNEL_ID,
  BEAMER_CHANNEL_TITLE,
  UNREAD_COUNT_DISPLAY_THRESHOLD,
} from 'gcs-common/constants/channelConstants';
import {
  getChannelImage,
  getChannelStatus,
  getChannelSubtitle,
  getChannelTitle,
  getIsHidden,
  getMainContactId,
  getMemberAgentIdsForChannel,
  getNumOpenTasksForChannel,
  getSalesSpaceMostAvailableAgent,
} from 'gcs-common/slices/channel/channelSelectors';
import { getSearchInput } from 'gcs-common/slices/channelList/channelListSelectors/getSearchInput';
import { getSelectedChannelId } from 'gcs-common/slices/channels/channelsSelectors';
import changeSelectedChannelId from 'gcs-common/slices/channels/channelsThunks/changeSelectedChannelId';
import { getUnreadMessageCountForChannel } from 'gcs-common/slices/channels/unreadMessagesSelectors';
import { getCurrentUserId } from 'gcs-common/slices/currentUser/currentUserSelectors';
import {
  getChannelIsFreeSalesSpace,
  getCurrentUserIsMemberOfChannel,
  getSalesSpaceChannelProcessing,
} from 'gcs-common/slices/salesSpaceChannels/salesSpaceChannelsSelectors';
import joinSalesSpace from 'gcs-common/slices/salesSpaceChannels/salesSpaceChannelsThunks/joinSalesSpace';
import leaveSalesSpace from 'gcs-common/slices/salesSpaceChannels/salesSpaceChannelsThunks/leaveSalesSpace';
import { getFavicon } from 'gcs-common/slices/theme/themeSelectors';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Highlight from '../Highlight/Highlight';
import ImageWithStatus from '../ImageWithStatus/ImageWithStatus';
import styles from './styles.module.scss';

interface SalesSpaceChannelListItemProps {
  channelId: string;
}

const SalesSpaceChannelListItem = (props: SalesSpaceChannelListItemProps) => {
  const { channelId } = props;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const logo = useSelector(getFavicon);
  const selectedChannelId = useSelector(getSelectedChannelId);
  const channelImage = useSelector(getChannelImage(channelId));
  const channelStatus = useSelector(getChannelStatus(channelId));
  const channelTitle = useSelector(getChannelTitle(channelId));
  const channelSubtitle = useSelector(getChannelSubtitle(channelId));
  const searchInput = useSelector(getSearchInput);
  const isProcessing = useSelector(getSalesSpaceChannelProcessing(channelId));
  const isFree = useSelector(getChannelIsFreeSalesSpace(channelId));
  const isMember = useSelector(getCurrentUserIsMemberOfChannel(channelId));
  const agentIds = useSelector(getMemberAgentIdsForChannel(channelId));
  const mostAvailableAgent = useSelector(getSalesSpaceMostAvailableAgent(channelId));
  const mainContactPerson = useSelector(getMainContactId(channelId));
  const currentUserId = useSelector(getCurrentUserId);
  const isHidden = useSelector(getIsHidden(channelId));
  const numOpenTasks = useSelector(getNumOpenTasksForChannel(channelId));
  const ticketNotificationsCount = useGetETSTicketNotificationsCountForChannelSelector(channelId);
  const isBeamerChatEnabled = useGetBeamerChatEnabled();
  const tasksAndTickets = numOpenTasks + ticketNotificationsCount;

  const isMainContactPerson = mainContactPerson === currentUserId;
  const otherAgentsCount = agentIds.length - (isMember ? 1 : 0);
  const isSelected = channelId === selectedChannelId;

  const numUnreadMessages = useSelector(getUnreadMessageCountForChannel(channelId));

  const onChannelSelected = () => {
    dispatch(changeSelectedChannelId({ selectedChannelId: channelId }));
  };

  const channelStyle = useMemo(() => {
    if (isSelected) {
      return styles.selectedSalesSpaceChannel;
    }
    return styles.unselectedSalesSpaceChannel;
  }, [isSelected]);

  const onJoinSalesSpace = async () => {
    // @ts-expect-error redux
    await dispatch(joinSalesSpace({ channelId, currentUserId }));
    queryClient.invalidateQueries({ queryKey: [ChannelClientQueryKeys.INFO_PANEL_DETAILS, channelId] });
  };

  const onLeaveSalesSpace = async () => {
    // @ts-expect-error redux
    await dispatch(leaveSalesSpace({ channelId }));
    queryClient.invalidateQueries({ queryKey: [ChannelClientQueryKeys.INFO_PANEL_DETAILS, channelId] });
  };

  const renderedUnread =
    numUnreadMessages > UNREAD_COUNT_DISPLAY_THRESHOLD ? `${UNREAD_COUNT_DISPLAY_THRESHOLD}+` : numUnreadMessages;

  if (channelId === BEAMER_CHANNEL_ID) {
    if (!isBeamerChatEnabled) return null;
    return (
      <button type="button" onClick={onChannelSelected} className={channelStyle}>
        <div className={`${styles.channelFreeIndicator}`} />
        <div className={styles.channelInfo}>
          <div className={styles.userImage}>
            <ImageWithStatus isSalesSpace isAgent={false} className={styles.imageWrapper} imageUrl={logo} />
          </div>
          <div className={styles.content}>
            <div className={styles.customerTitle}>
              <Highlight search={searchInput}>{BEAMER_CHANNEL_TITLE}</Highlight>
            </div>
          </div>
          <div className={styles.unreadMsgContainer}>
            {numUnreadMessages > 0 && (
              <div className={styles.flexContainerRight}>
                <div className={styles.unreadMsgIndicator}>{renderedUnread}</div>
              </div>
            )}
          </div>
        </div>
      </button>
    );
  }

  return (
    <button type="button" onClick={onChannelSelected} className={channelStyle}>
      <div className={`${styles.channelFreeIndicator} ${isFree && styles.channelFreeIndicatorActive}`} />
      <div className={styles.channelInfo}>
        <div className={styles.userImage}>
          <ImageWithStatus
            isSalesSpace
            isAgent
            className={styles.imageWrapper}
            imageUrl={channelImage}
            userStatus={channelStatus}
          />
        </div>
        <div className={styles.content}>
          <div className={styles.customerTitle}>
            <Highlight search={searchInput}>{channelTitle}</Highlight>
          </div>
          <div className={styles.customerCompany}>
            <Highlight search={searchInput}>{channelSubtitle}</Highlight>
          </div>
          {isHidden && <div className={styles.archivedIndicator}>Archiviert</div>}
        </div>
        <div className={styles.unreadMsgContainer}>
          {(numUnreadMessages > 0 || tasksAndTickets > 0) && (
            <div className={styles.flexContainerRight}>
              {numUnreadMessages > 0 && <div className={styles.unreadMsgIndicator}>{renderedUnread}</div>}

              {tasksAndTickets > 0 && <div className={styles.todoIndicator}>{tasksAndTickets}</div>}
            </div>
          )}

          <div className={styles.imageStatusAndButtonContainer}>
            {otherAgentsCount > 0 && (
              <>
                <div>
                  <ImageWithStatus
                    isSmall
                    isDot
                    imageUrl={mostAvailableAgent?.imageUrl}
                    userStatus={mostAvailableAgent?.status}
                  />
                </div>

                {otherAgentsCount > 1 && (
                  <div className={`${styles.agentCountIndicator} ${isSelected ? styles.white : ''}`}>
                    +{otherAgentsCount - 1}
                  </div>
                )}
              </>
            )}

            <div className={styles.buttonContainer}>
              {isMember && !isMainContactPerson && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={onLeaveSalesSpace}
                  className={`${styles.leaveButton} ${isProcessing && styles.processing}`}
                >
                  {/* Div here instead of button because it's nested in a button */}
                  Verlassen
                </div>
              )}
              {!isMember && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={onJoinSalesSpace}
                  className={`${styles.joinButton} ${isProcessing && styles.processing}`}
                >
                  {/* Div here instead of button because it's nested in a button */}
                  Beitreten
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
};

export default SalesSpaceChannelListItem;
